import { debounce } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { usePostIsValidCreateConceptGroupNameMutation } from '@verantos-dev/data-access-concept-groups';

import { NameValidationStatus } from '../components/ConceptGroupNameInput';

export const useValidateName = (conceptGroupName?: string, projectId?: string) => {
  const intl = useIntl();
  const [validateName, { isLoading, isError, data }] =
    usePostIsValidCreateConceptGroupNameMutation();
  const debounceValidate = useRef(
    debounce(async (conceptGroupName, projectId) => {
      if (conceptGroupName?.trim()) {
        await validateName({ conceptGroupName, projectId }).unwrap();
      }
    }, 200)
  ).current;

  useEffect(() => {
    debounceValidate(conceptGroupName, projectId);
  }, [debounceValidate, conceptGroupName, projectId]);

  const nameValidationStatus: NameValidationStatus | undefined = useMemo(() => {
    if (!conceptGroupName?.trim()) return undefined;
    if (isLoading) return NameValidationStatus.Pending;
    if (isError) return NameValidationStatus.Error;
    if (data) return data.isValid ? NameValidationStatus.Valid : NameValidationStatus.Invalid;
  }, [conceptGroupName, data, isError, isLoading]);

  const nameValidationError: string | undefined = useMemo(() => {
    if (isError) return intl.formatMessage({ id: 'conceptGroup.nameInput.error' });
    if (data && data.isValid === false)
      return intl.formatMessage({ id: 'conceptGroup.nameInput.nameExists' });
  }, [data, intl, isError]);

  return {
    nameValidationStatus,
    nameValidationError,
  };
};
